import { useCallback, useMemo, useRef } from 'react';

// Last-write-wins guard for parallel async operations.
//
// Returns `next()` to bump a ref counter and capture an id, plus
// `isLatest(id)` to check whether a previously-captured id is still the
// most recent. Use this to drop stale results from older parallel fetches
// that resolve out-of-order:
//
//   const reqId = useRequestId();
//   const fetchX = useCallback(async () => {
//     const myId = reqId.next();
//     setLoading(true);
//     try {
//       const { data, error } = await supabase.from('x').select();
//       if (!reqId.isLatest(myId)) return;        // a newer fetch took over
//       if (error) { console.error(error); return; }
//       if (!data) { console.warn('null data, no error'); return; }
//       setData(data);
//     } finally {
//       if (reqId.isLatest(myId)) setLoading(false);
//     }
//   }, [...]);
//
// Why not AbortController? Many of our fetches are Supabase RPCs that
// can't be aborted server-side, and we want to also gate background
// follow-ups (count enrichment, etc.) — a request id covers both cases.
export function useRequestId() {
  const ref = useRef(0);
  const next = useCallback(() => ++ref.current, []);
  const isLatest = useCallback((id: number) => id === ref.current, []);
  // CRITICAL: return a memoized object so the wrapper reference is stable
  // across renders. Without this, every render returns a new {next,isLatest}
  // object, so consumers that include the result in useCallback/useEffect
  // dependencies see a fresh identity every render and re-fire infinitely.
  // Production incident 2026-05-05 saw 87 req/s sustained from a single
  // browser tab because CRMContext's data-load effect was caught in this
  // exact loop.
  return useMemo(() => ({ next, isLatest }), [next, isLatest]);
}
